type ScreenSizeName = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const range = (start: number, end: number) =>
  Array(end)
    .fill(null)
    .map((_x, i) => start + i)

const generateClasses = (
  property: string,
  values: (string | number)[],
  responsive: boolean = true,
  negative?: boolean,
) => {
  const classes = []

  for (const value of values) {
    const c = `${property}-${value}`
    classes.push(c)

    if (negative) classes.push(`-${c}`)

    if (responsive) {
      for (const screen of ['xs', 'sm', 'md', 'lg', 'xl']) {
        classes.push(`${screen}:${c}`)
        if (negative) classes.push(`-${screen}:${c}`)
      }
    }
  }

  return classes
}

const colorSafelist = [
  'gray',
  'pink-faded',
  'pink',
  'pink-hot',
  'green-light',
  'green',
  'green-dark',
  'green-electric',
  'blue',
  'red',
  'yellow',
]

const spacingSafelist = [
  0, 1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24, 32, 40, 48, 56, 64,
]

export const safelist = [
  ...generateClasses('text', ['left', 'center', 'right', 'justify']),
  ...generateClasses('animate', ['none', 'spin', 'ping', 'pulse', 'bounce']),
  ...generateClasses('grid-cols', [...range(1, 12), 'none']),
  ...generateClasses('col-span', [...range(1, 12), 'full']),
  ...generateClasses('col-start', [...range(1, 13), 'auto']),
  ...generateClasses(
    'order',
    [...range(0, 37), 'first', 'last', 'none'],
    true,
    true,
  ),
  ...generateClasses('self', ['auto', 'start', 'end', 'center', 'stretch']),
  ...generateClasses('justify-self', [
    'auto',
    'start',
    'end',
    'center',
    'stretch',
  ]),
  ...generateClasses('max-w', [
    '0',
    'none',
    'xs',
    'sm',
    'md',
    'lg',
    'xl',
    '2xl',
    '3xl',
    '4xl',
    '5xl',
    '6xl',
    '7x',
    'full',
    'min',
    'max',
    'prose',
    'screen-sm',
    'screen-md',
    'screen-lg',
    'screen-xl',
    'screen-2xl',
  ]),
  ...generateClasses('min-h', ['screen', 'xs', 'sm', 'md', 'lg', 'xl']),
  ...generateClasses('pt', [
    '[100%]',
    '[calc((3/4)*100%)]',
    '[calc((7/5)*100%)]',
    '[calc((6/4)*100%)]',
    '[calc((9/16)*100%)]',
    '[calc((7/16)*100%)]',
  ]),
  ...generateClasses('pt', spacingSafelist, true),
  ...generateClasses('pb', spacingSafelist, true),
  ...generateClasses('pr', spacingSafelist, true),
  ...generateClasses('pl', spacingSafelist, true),
  ...generateClasses('mt', spacingSafelist, true),
  ...generateClasses('mb', spacingSafelist, true),
  ...generateClasses('mr', spacingSafelist, true),
  ...generateClasses('ml', spacingSafelist, true),
  ...generateClasses('text', colorSafelist),
  ...generateClasses('bg', colorSafelist, true),
  ...generateClasses('before:bg', colorSafelist, true),
  ...generateClasses('after:bg', colorSafelist, true),
]

export const pixelsPerRem = 16

export const screens: Record<ScreenSizeName, string> = {
  xs: '480px',
  sm: '768px',
  md: '940px',
  lg: '1200px',
  xl: '1600px',
}

// Font sizes in rem units
export const fontSizes = {
  xs: '0.75rem',
  sm: '0.875rem',
  base: '1rem',
  lg: '1.125rem',
  xl: '1.375rem',
  '2xl': '1.625rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '2.875rem',
  '6xl': '3.75rem',
  '7xl': '4.375rem',
  '8xl': '6rem',
}

/**
 * Gets clamped font size value based on screen size.
 */
export const getClampedFontSizeValue = (
  minFontSizeInRem: number,
  maxFontSizeInRem: number,
  minScreenSizeInPixels: number,
  maxScreenSizeInPixels: number,
) => {
  const minScreenSizeInRem = minScreenSizeInPixels / pixelsPerRem
  const maxScreenSizeInRem = maxScreenSizeInPixels / pixelsPerRem
  const slope =
    (maxFontSizeInRem - minFontSizeInRem) /
    (maxScreenSizeInRem - minScreenSizeInRem)
  const yAxisIntersection = minFontSizeInRem - minScreenSizeInRem * slope

  return `clamp(${minFontSizeInRem}rem, ${yAxisIntersection}rem + ${
    slope * 100
  }vw, ${maxFontSizeInRem}rem )`
}

export const getColors = () => {
  const colors = {
    transparent: 'transparent',
    current: 'currentColor',
    black: '#000000',
    white: '#FFFFFF',
    gray: '#F0F0F0',
    pink: {
      faded: '#DAB0D2',
      DEFAULT: '#E9AFF1',
      hot: '#E6379B',
    },
    green: {
      light: '#B8F483',
      DEFAULT: '#56BC7A', // Where did this appear from? It's not in the brandbook.
      dark: '#01424F',
      electric: '#18DC82',
    },
    blue: '#2D46AF',
    yellow: '#EFDC2C',
    red: '#F73424',
  }

  return {
    ...colors,
    pageBG: colors.white,
    pageText: colors.black,
    backdrop: colors.black,
    header: {
      bg: colors.white,
      text: colors.blue,
    },
    btn: {
      filled: {
        default: {
          text: {
            DEFAULT: colors.black,
            hover: colors.black,
          },
          bg: {
            DEFAULT: colors.green.light,
            hover: colors.green.light,
          },
          border: {
            DEFAULT: colors.green.light,
            hover: colors.green.light,
          },
        },
      },
      outlined: {
        default: {
          text: {
            DEFAULT: colors.green.light,
            hover: colors.black,
          },
          bg: {
            DEFAULT: colors.transparent,
            hover: colors.green.light,
          },
          border: {
            DEFAULT: colors.green.light,
            hover: colors.green.light,
          },
        },
      },
    },
    input: {
      border: colors.black,
      text: colors.black,
      bg: colors.transparent,
      inverted: {
        border: colors.white,
        text: colors.white,
        bg: colors.transparent,
      },
    },
    success: {
      DEFAULT: colors.green.DEFAULT,
      bg: '#ECFFEC',
    },
    error: {
      DEFAULT: '#D84949',
      bg: '#FFF0F0',
    },
  }
}
